import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Shield, Truck, Headphones, Clock, Award, MapPin } from "lucide-react";

export const Route = createFileRoute("/om-oss")({
  head: () => ({
    meta: [
      { title: "Om Event Sverige — Eventuthyrning i hela Sverige" },
      { name: "description", content: "Läs mer om Event Sverige — Sveriges nya alternativ för eventuthyrning. Hoppborgar, tivolispel och eventteknik med fokus på service och säkerhet." },
      { property: "og:title", content: "Om Event Sverige" },
      { property: "og:description", content: "Vi hyr ut eventutrustning med fokus på trygghet, smidighet och oförglömliga upplevelser." },
      { property: "og:url", content: "/om-oss" },
    ],
    links: [{ rel: "canonical", href: "https://eventsverige.se/om-oss" }],
  }),
  component: OmOssPage,
});

const VALUES = [
  { icon: Shield, title: "Säkerhet först", text: "All utrustning är besiktigad, försäkrad och underhållen — uppblåsbara redskap enligt SS-EN 14960-1:2019. Vi tar inga genvägar när det gäller säkerhet." },
  { icon: Truck, title: "Leverans & rigg", text: "Vi levererar, riggar och hämtar — så du kan fokusera på eventet." },
  {
    icon: Clock,
    title: "Snabb offert",
    text: "Vi går igenom era önskemål och återkommer med ett skräddarsytt prisförslag.",
  },
  { icon: Headphones, title: "Personlig service", text: "Du pratar alltid med en människa — inte en chatbot eller ett formulär." },
];

function OmOssPage() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 bg-grain opacity-20" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-22">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 text-accent px-4 py-1.5 text-xs font-bold uppercase tracking-widest mb-6">
              <Award className="h-3.5 w-3.5" />
              Om oss
            </div>
            <h1 className="font-display text-5xl sm:text-6xl font-extrabold leading-[0.95]">
              Om <span className="text-accent">Event Sverige</span>
            </h1>
            <p className="mt-6 text-lg text-primary-foreground/80 max-w-xl">
              Event Sverige är Sveriges nya alternativ för eventuthyrning. Vi startade med en enkel idé:
              att det ska vara lika smidigt att hyra en hoppborg som att beställa en taxi.
              Inga krångliga avtal, inga dolda avgifter — bara schyssta priser och snabb service.
            </p>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid gap-12 lg:grid-cols-2 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-accent">Vår historia</span>
            <h2 className="mt-2 font-display text-4xl sm:text-5xl font-extrabold">
              Från ett garage till 500+ event
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Event Sverige grundades av ett gäng eventnördar som tröttnat på krångliga uthyrningsprocesser
              och otydliga priser. Vi började med en enda hoppborg och en dröm — att göra event
              tillgängliga för alla, oavsett budget.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Idag har vi ett växande sortiment med allt från uppblåsbara hinderbanor till professionellt
              ljud och ljus. Vi har levererat till skolavslutningar, företagskickoffer, festivaler och
              privata fester över hela Sverige.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Det som inte har förändrats? Vårt fokus på säkerhet, punktlighet och att varje kund ska känna
              sig som en VIP — oavsett om det är ett litet kalas eller en stor mässa.
            </p>
          </div>
          <div className="rounded-3xl bg-secondary/50 border border-border p-8 sm:p-12">
            <div className="grid grid-cols-2 gap-6">
              {[
                { n: "500+", l: "Genomförda event" },
                { n: "15+", l: "Produkter i sortimentet" },
                { n: "3", l: "Kategorier" },
                { n: "Offert", l: "Pris efter era önskemål" },
              ].map((s) => (
                <div key={s.l} className="text-center">
                  <div className="font-display text-4xl font-extrabold text-primary">{s.n}</div>
                  <div className="text-sm text-muted-foreground mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-secondary/50 border-y border-border">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs font-bold uppercase tracking-widest text-accent">Våra värderingar</span>
            <h2 className="mt-2 font-display text-4xl sm:text-5xl font-extrabold">
              Så arbetar vi
            </h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="rounded-2xl bg-card border border-border p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                  <div className="h-12 w-12 rounded-xl bg-accent/15 grid place-items-center mb-5">
                    <Icon className="h-6 w-6 text-accent-foreground" />
                  </div>
                  <h3 className="font-display text-xl font-bold">{v.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="relative overflow-hidden rounded-3xl bg-primary text-primary-foreground p-10 sm:p-16">
          <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-accent/20 blur-3xl" aria-hidden />
          <div className="relative max-w-2xl">
            <h2 className="font-display text-3xl sm:text-5xl font-extrabold">
              Redo att planera ditt nästa event?
            </h2>
            <p className="mt-4 text-primary-foreground/80 text-lg">
              Oavsett om du arrangerar ett litet kalas eller en stor företagsfest,
              har vi utrustningen och kompetensen att hjälpa dig.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/produkter"
                className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-bold hover:bg-accent/90"
              >
                Se vårt sortiment <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/30 px-6 py-3 font-semibold hover:bg-primary-foreground/10"
              >
                Kontakta oss
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
